import { Button } from '@app/components/various/Button';
import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, Form, FormFieldset, FormGrid, InputField, ToggleField } from '@app/components/various/Form';
import { StickyModalFooter } from '@app/components/various/Modal';
import { useCreateLookbook } from '@app/services/hooks/lookbook/useCreateLookbook';
import { useMarkets } from '@app/services/hooks/market/useMarkets';
import { useAmplitude, useModalVisibility } from '@app/utils/hooks';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { Lookbook } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { CreateLookbookFormOptions } from './CreateLookbookFormOptions';

const defaultValues: Lookbook.CreateLookbookForm = {
  allowAccountRequest: true,
  deliveryWindows: [],
  market: '',
  name: '',
  pricelists: [],
};

export const CreateLookbookForm = () => {
  const { t } = useTranslation(['common', 'validation', 'lookbook']);

  const { hideModal } = useModalVisibility('CreateLookbookModal');
  const { data, isLoading } = useMarkets();

  const { mutate: createLookbook, isPending } = useCreateLookbook();
  const { trackEvent } = useAmplitude();

  const markets = React.useMemo(() => data ?? [], [data]);

  const validationSchema: yup.ObjectSchema<Lookbook.CreateLookbookForm> = yup.object({
    allowAccountRequest: yup.boolean().defined(),
    deliveryWindows: yup.array(yup.string().defined()).defined(),
    market: yup.string().required(t('validation:market_hint')),
    name: yup.string().defined(),
    pricelists: yup.array(yup.string().defined()).defined(),
  });

  const formMethods = useValidatedForm<Lookbook.CreateLookbookForm>({ defaultValues, validationSchema });
  const { register, resetField } = formMethods;

  const handleMarketIdChange = React.useCallback(() => {
    resetField('deliveryWindows');
  }, [resetField]);

  const handleSubmit = (values: Lookbook.CreateLookbookForm) => {
    createLookbook(values, {
      onSuccess: () => {
        trackEvent({
          name: 'lookbookCreateModal.create.additionalCriteria.provided',
          properties: { deliweryWindows: values.deliveryWindows.length > 0, pricelists: values.pricelists.length > 0 },
        });
        hideModal();
      },
    });
  };

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit}>
      <FormFieldset isDisabled={isPending}>
        <FormGrid>
          <InputField label={t('common:name')} placeholder={t('lookbook:lookbook_name')} isRequired={false} {...register('name')} />
          <AutocompleteField
            isRequired
            overflowContainer
            name="market"
            label={t('common:market')}
            placeholder={t('common:select_market')}
            onChange={handleMarketIdChange}
            isLoading={isLoading}
          >
            {markets.map(({ id, name }) => (
              <Option key={id} value={id}>
                {name}
              </Option>
            ))}
          </AutocompleteField>
        </FormGrid>
      </FormFieldset>
      <FormFieldset isDisabled={isPending}>
        <ToggleField
          label={t('lookbook:allow_account_requesting')}
          orientation="horizontal"
          {...register('allowAccountRequest')}
          disabled={isPending}
          fieldLabelClassName="w-fit"
        />
      </FormFieldset>
      <CreateLookbookFormOptions isDisabled={isPending} />
      <StickyModalFooter>
        <div className="flex justify-end gap-2">
          <Button color="secondary" variant="ghost" disabled={isPending} onClick={hideModal}>
            {t('common:cancel')}
          </Button>
          <Button type="submit" isLoading={isPending}>
            {t('common:create')}
          </Button>
        </div>
      </StickyModalFooter>
    </Form>
  );
};
